import EmptyState from "@/components/empty-state/EmptyState.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Checkbox,
  Input,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import {
  ApiStageInPipelineEdit,
  getPipelineStageTimeLimits,
} from "@/pages/settings/recruit/pipelines/widgets/pipeline-edit-modal.types.ts";
import { usePipelineSave } from "@/queries/recruit/use-pipelines.query.ts";
import { useStages } from "@/queries/recruit/use-stages.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiPipeline,
  ApiPipelineSavePayload,
  ApiPipelineStagePayload,
} from "@/types/recruit/pipeline.types.ts";
import { PipelineStageTypes } from "@/types/recruit/stages.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface PipelineEditModalProps extends ModalProps {
  pipeline: ApiPipeline;
}

export function PipelineEditModal({ pipeline, ...props }: PipelineEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const pipelineSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    is_default: z.boolean(),
    stages: z.array(
      z.object({
        id: z.number(),
        sla_limit: z.number(),
      })
    ),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(pipelineSchemaObject),
    defaultValues: {
      title: pipeline.title,
      is_default: pipeline.is_default,
      stages:
        pipeline.stages.map((item) => {
          return {
            id: item.id,
            sla_limit: item.sla_limit,
            selected: item.type != PipelineStageTypes.custom,
          };
        }) || [],
    },
  });

  const { data: stages } = useStages();
  const pipelineSave = usePipelineSave(control);

  const [stagesConfig, setStagesConfig] = useState<ApiStageInPipelineEdit[]>(
    pipeline.stages.map((stage) => {
      return {
        id: stage.id,
        title: stage.title,
        sla_limit: stage.sla_limit.toString(),
        selected: true,
        type: stage.type,
      };
    })
  );

  const selectedStagesRows = useMemo(() => {
    if (!stages) return {};
    const result: RowSelectionState = {};
    for (let i = 0; i < stages.length; i++) {
      if (stagesConfig.find((stage) => stage.id === stages[i].id)) result[i] = true;
      if (stages[i].type != PipelineStageTypes.custom) result[i] = true;
    }
    return result;
  }, [stages]);

  const stagesInPipelines = useMemo(() => {
    return (
      stages?.map((stage) => {
        const stageConfig = stagesConfig.find((item) => item.id === stage.id);
        const item: ApiStageInPipelineEdit = {
          ...stage,
          sla_limit: stageConfig?.sla_limit || "0",
          selected: stageConfig?.selected || stage.type != PipelineStageTypes.custom,
        };
        return item;
      }) || []
    );
  }, [stages, stagesConfig]);

  const onSubmit = handleSubmit((pipelineData) => {
    if (!stages) return;
    const stagesWithSorts: ApiPipelineStagePayload[] = stagesInPipelines
      .filter((stage) => stage.selected)
      .map((stage) => {
        const stagePayload: ApiPipelineStagePayload = {
          id: stage.id,
          sla_limit: parseInt(stage.sla_limit, 10) || 0,
        };
        return stagePayload;
      });

    const payload: ApiPipelineSavePayload = {
      title: pipelineData.title,
      is_default: pipelineData.is_default,
      stages: stagesWithSorts,
    };

    pipelineSave.mutate(
      { id: pipeline.id, payload },
      {
        onSuccess: (data) => {
          if (pipeline.id == 0) {
            analyticsService.trackEvent(analyticEvents.pipelines.created, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.pipelines.edited, {
              [analyticProperties.id]: pipeline.id,
            });
          }
          close(id);
        },
      }
    );
  });

  const handleRowSelectionChange = (selectedRows: RowSelectionState) => {
    const newStagesConfig = stagesInPipelines.map((stage, index) => ({
      ...stage,
      selected: selectedRows[index.toString()] || stage.type != PipelineStageTypes.custom,
    }));
    setStagesConfig(newStagesConfig);
  };

  const handleChangeTimeLimit = (stageId: number, sla_limit: number) => {
    const newStagesConfig = stagesInPipelines.map((stage) => {
      if (stage.id === stageId) {
        return { ...stage, sla_limit: sla_limit.toString() };
      }
      return stage;
    });
    setStagesConfig(newStagesConfig);
  };

  // const columnHelper = createColumnHelper<ApiStageInPipeline>();
  const columns = useMemo<ColumnDef<ApiStageInPipelineEdit>[]>(
    () => [
      {
        id: "select-col",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect() || row.original.type != PipelineStageTypes.custom}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        ...ColumnSizes.tiny,
      },
      {
        header: t("common:title"),
        cell: (row) => <Text className={"text-title py-4 block"}>{row.row.original.title}</Text>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("ats:time_limit_title"),
        cell: (row) =>
          row.row.original.selected && (
            <Select
              variant={"plain"}
              emptyTitle={""}
              options={getPipelineStageTimeLimits()}
              value={row.row.original.sla_limit}
              onChange={(value) => {
                handleChangeTimeLimit(row.row.original.id, value as number);
              }}
            ></Select>
          ),
        accessorKey: "time_limit",
        ...ColumnSizes.standard,
      },
    ],
    [stagesInPipelines]
  );
  return (
    <Modal
      {...props}
      title={t("ats:pipeline")}
      withCloser
      size={"lg"}
      layout={"stretch"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={pipelineSave.isPending}
            disabled={!stages}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          autoFocus={true}
          type="text"
          {...register("title")}
          error={errors.title?.message}
        />
        <Stack direction={"horizontal"} className={"items-center h-14"} gap={"sm"}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Switch checked={value} onChange={onChange} title={t("ats:pipeline_default")} />
            )}
            control={control}
            name={"is_default"}
          />
        </Stack>
      </form>

      <Title
        header={t("ats:pipelines_select_stages_title")}
        size={"md"}
        caption={t("ats:pipelines_select_stages_description")}
        paddingBottom
        paddingTop
      />

      <Table
        data={stagesInPipelines || []}
        rowSelection={selectedStagesRows}
        onRowSelectionChange={handleRowSelectionChange}
        columns={columns}
        enableMultiRowSelection={true}
        emptyStateComponent={<EmptyState />}
      />
    </Modal>
  );
}
